import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect } from 'react';
import { useNotificationSettingsQuery } from '@gov-nx/api';
import {
	usePersonCommunicationData,
	useProcessControl,
} from '@gov-nx/core/hooks';
import { useDataBoxStoreLists } from '@gov-nx/store';
import { useConfirmModal } from '@gov-nx/utils/common';
import {
	DataBoxNotificationToggleInputName,
	FormInstance,
	setExtendedNotification,
	setNotificationCheckboxesValues,
} from './FormDefinitions';
import type { DataboxNotificationSettingsContext } from './context.types';
import { notificationSettingsValues } from './utils';

export const DatovkaNastaveniUpozorneniContext =
	createContext<DataboxNotificationSettingsContext | null>(null);

interface DatovkaNastaveniUpozorneniContextProviderProps {
	databoxId: string;
	children: ReactNode;
}

export function DatovkaNastaveniUpozorneniContextProvider({
	databoxId,
	children,
}: DatovkaNastaveniUpozorneniContextProviderProps) {
	const { emailVerified, phoneVerified } = usePersonCommunicationData();
	const { controls, setControls } = useProcessControl();
	const {
		isConfirmModalOpen,
		showConfirmModal,
		handleModalConfirm,
		handleModalCancel,
	} = useConfirmModal({
		onConfirmed: () => {
			setExtendedNotification(formInstance, databoxId, true);
			storeNotificationSettings();
		},
		onCanceled: () => {
			setExtendedNotification(formInstance, databoxId, false);
		},
	});

	const { getDataBoxById } = useDataBoxStoreLists();
	const databox = getDataBoxById(databoxId);

	const notificationSettingQuery = useNotificationSettingsQuery(databoxId);

	const formInstance = FormInstance(databoxId);

	useEffect(() => {
		if (notificationSettingQuery.isSuccess && notificationSettingQuery.data) {
			setControls({ initialLoading: false });

			const values = notificationSettingsValues.decompose(
				notificationSettingQuery.data.hodnota
			);

			formInstance.reset({
				[databoxId]: values,
			});
		}
	}, [
		databoxId,
		formInstance,
		notificationSettingQuery.data,
		notificationSettingQuery.isSuccess,
		setControls,
	]);

	const storeNotificationSettings = useCallback(() => {
		const newValue = notificationSettingsValues.compose(
			formInstance.getValues(databoxId)
		);
		setControls({ processLoading: true });

		notificationSettingQuery.mutate(
			{ value: newValue },
			{
				onSuccess: () => {
					setControls({ processLoading: false });
				},
				onError: async (error) => {
					setControls({ processError: error, processLoading: false });
					formInstance.reset(formInstance.control._defaultValues);
				},
			}
		);

		// Update checkboxes to reflect the new value (when email is disabled, email-attachment should be disabled too)
		setNotificationCheckboxesValues(
			formInstance,
			databoxId,
			notificationSettingsValues.decompose(newValue)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [databoxId, formInstance, notificationSettingQuery.mutate, setControls]);

	const handleExtendedMailNotificationChange = useCallback(() => {
		const isExtendedMailChecked =
			formInstance.getValues(databoxId)?.[
				DataBoxNotificationToggleInputName.EmailAttachment
			];
		if (isExtendedMailChecked) {
			showConfirmModal();
		} else {
			storeNotificationSettings();
		}
	}, [databoxId, formInstance, storeNotificationSettings, showConfirmModal]);

	return (
		<DatovkaNastaveniUpozorneniContext.Provider
			value={{
				controls,
				formInstance,
				databox,
				storeNotificationSettings,
				handleExtendedMailNotificationChange,
				isConfirmModalOpen,
				handleModalConfirm,
				handleModalCancel,
				noContactDetails: !emailVerified && !phoneVerified,
				disableEmailOptions: !emailVerified,
				disablePhoneOptions: !phoneVerified,
				isEmailAttachmentChecked:
					formInstance.getValues(databoxId)?.[
						DataBoxNotificationToggleInputName.EmailAttachment
					],
			}}>
			{children}
		</DatovkaNastaveniUpozorneniContext.Provider>
	);
}

export const useDatovkaNastaveniUpozorneniContext = () =>
	useContext(
		DatovkaNastaveniUpozorneniContext
	) as DataboxNotificationSettingsContext;
